import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';

import { getExercisesQuery } from 'modules/exercises/graphql/queries/getExercisesQuery';

import { MultipleChoiceExerciseInput } from '../types/form';
import { MultipleChoiceExercise } from '../types';
import {
  createMultipleChoiceExerciseMutation,
  CreateMultipleChoiceExerciseMutationResult,
  CreateMultipleChoiceExerciseMutationVariables,
} from '../graphql/mutations/createMultipleChoiceExerciseMutation';
import {
  updateMultipleChoiceExerciseMutation,
  UpdateMultipleChoiceExerciseMutationResult,
  UpdateMultipleChoiceExerciseMutationVariables,
} from '../graphql/mutations/updateMultipleChoiceExerciseMutation';

import { transformFormInputToMutationInput } from '../utils';

interface MultipleChoiceExerciseActionsHookInput {
  courseId: string;
  subjectId: string;
}

interface MultipleChoiceExerciseActionsHookOutput {
  onCreateMultipleChoiceExercise: (input: MultipleChoiceExerciseInput, onSuccess: (exercise: MultipleChoiceExercise) => void) => void;
  createMultipleChoiceExerciseLoading: boolean;
  createMultipleChoiceExerciseError?: string;

  onUpdateMultipleChoiceExercise: (exerciseId: string, input: MultipleChoiceExerciseInput) => void;
  updateMultipleChoiceExerciseLoading: boolean;
  updateMultipleChoiceExerciseError?: string;
}

export default function useMultipleChoiceExerciseActions(input: MultipleChoiceExerciseActionsHookInput): MultipleChoiceExerciseActionsHookOutput {
  const { courseId, subjectId } = input;
  const { showErrorNotification, showSuccessNotification } = useSnackbarNotifications();
  const intl = useIntl();

  /**
   * Create
   */
  const [createMultipleChoiceExercise, { loading: createMultipleChoiceExerciseLoading, error: createMultipleChoiceExerciseError }] = useMutation<
    CreateMultipleChoiceExerciseMutationResult,
    CreateMultipleChoiceExerciseMutationVariables
  >(createMultipleChoiceExerciseMutation, {
    onError: showErrorNotification,
    onCompleted: () => showSuccessNotification(intl.formatMessage({ id: 'notification.exerciseSaved' })),
    refetchQueries: [{ query: getExercisesQuery, variables: { courseId, subjectId } }],
  });
  const onCreateMultipleChoiceExercise = useCallback(
    (input: MultipleChoiceExerciseInput, onSuccess: (exercise: MultipleChoiceExercise) => void) => {
      createMultipleChoiceExercise({
        variables: {
          courseId,
          subjectId,
          input: transformFormInputToMutationInput(input),
        },
      }).then(({ data }) => {
        if (data?.multipleChoiceExercise) {
          onSuccess(data.multipleChoiceExercise);
        }
      });
    },
    [courseId, subjectId, createMultipleChoiceExercise]
  );

  /**
   * Update
   */
  const [updateMultipleChoiceExercise, { loading: updateMultipleChoiceExerciseLoading, error: updateMultipleChoiceExerciseError }] = useMutation<
    UpdateMultipleChoiceExerciseMutationResult,
    UpdateMultipleChoiceExerciseMutationVariables
  >(updateMultipleChoiceExerciseMutation, {
    onError: showErrorNotification,
    onCompleted: () => showSuccessNotification(intl.formatMessage({ id: 'notification.exerciseSaved' })),
  });
  const onUpdateMultipleChoiceExercise = useCallback(
    (exerciseId: string, input: MultipleChoiceExerciseInput) => {
      updateMultipleChoiceExercise({
        variables: {
          courseId,
          subjectId,
          exerciseId,
          input: transformFormInputToMutationInput(input),
        },
      });
    },
    [updateMultipleChoiceExercise, courseId, subjectId]
  );

  return {
    onCreateMultipleChoiceExercise,
    createMultipleChoiceExerciseLoading,
    createMultipleChoiceExerciseError: getErrorMessage({ error: createMultipleChoiceExerciseError }),

    onUpdateMultipleChoiceExercise,
    updateMultipleChoiceExerciseLoading,
    updateMultipleChoiceExerciseError: getErrorMessage({ error: updateMultipleChoiceExerciseError }),
  };
}
